import { VIEW_STATES } from './vars';
import {
    defaultToolTip, getYearsMarks, YEARS,
    getWaveYearsMarks, waveYEARS, waveToolTip,
    tosHistoricYears, tosHistoricToolTip, gettosHistoricMarks,
    tosSSP126Years, gettosSSP126Marks, tosSSP126ToolTip,
    tosSSP434Years, gettosSSP434Marks, tosSSP434ToolTip,
    tosSSP460Years, gettosSSP460Marks, tosSSP460ToolTip,
    tosSSP534OVERYears, gettosSSP534OVERMarks, tosSSP534OVERToolTip,
    tosSSP585Years, gettosSSP585Marks, tosSSP585ToolTip,
    historicCRWYears, historicCRWToolTip, getHistoricCRWMarks,
    currentCRWYears, currentCRWToolTip, getCurrentCRWMarks,
    getCurrentCRWNames,
    getHistoricCRWNames,
    createMarksGenerator
} from './components/vars';
import allForecasts from './forecast-data.json';
import {
    oisstIndoPacificDates,
    oisstPalauDates,
    oisstRegionMetadata,
    oisstRmiDates,
} from './oisstConfig';
import { oceanColorDates } from './oceanColorConfig';
import {
    cfsRegionMetadata,
    cfsVariableMetadata,
    cfsVariableOrder,
    cfsDates_sst,
    cfsDates_ssh,
    cfsDates_sss,
    cfsDates_current,
    cfsDates_pressure,
    cfsDates_wind,
    cfsDates_airtmp,
    cfsDates_spfh,
    cfsDates_precip,
} from './cfsConfig';
import {
    gfsRegionMetadata,
    gfsVariableMetadata,
    gfsVariableOrder,
    gfsDates_sst,
    gfsDates_pressure,
    gfsDates_wind,
    gfsDates_airtmp,
    gfsDates_spfh,
    gfsDates_precip,
} from './gfsConfig';
import { parseForecastId, isEkmanVariable } from './forecastMenuConfig';
import {
    ekmanCfsDates,
    ekmanGfsDates,
    ekmanMetopDates,
    ekmanVariableMetadata,
    ekmanRegionMetadata,
} from './ekmanConfig';
import { hurricaneActiveStorms } from './hurricaneConfig';
import { crwDailyDateCatalog, crwDailyDates, crwRmiAlertDates } from './crwConfig';
import { waveHindcastDates, waveHindcastMetadata } from './waveHindcastConfig';
import { waveContinuumTimeline } from './waveContinuumConfig';
import { regionalSeaLevelDates, regionalSeaLevelMetadata } from './regionalSeaLevelConfig';
import {
    APCC_VARIABLES,
    getApccRecords,
    parseApccId,
} from './apccConfig';

const CFS_DATES = {
    sst: cfsDates_sst,
    ssh: cfsDates_ssh,
    sss: cfsDates_sss,
    current: cfsDates_current,
    pressure: cfsDates_pressure,
    wind: cfsDates_wind,
    airtmp: cfsDates_airtmp,
    spfh: cfsDates_spfh,
    precip: cfsDates_precip,
};

const GFS_DATES = {
    sst: gfsDates_sst,
    pressure: gfsDates_pressure,
    wind: gfsDates_wind,
    airtmp: gfsDates_airtmp,
    spfh: gfsDates_spfh,
    precip: gfsDates_precip,
};

// One camera for every basin-scale SST product. The 0-360 longitude span is
// 92 E (western Myanmar) through 280 E (80 W, east of Florida).
const INDO_PACIFIC_SST_VIEW = Object.freeze({
    latitude: 5,
    longitude: 186,
    zoom: 1.9,
    bearing: 0,
    pitch: 0,
});

// OISST benefits from a closer basin framing than the forecast products. It
// still spans the warm-pool/convergence-zone domain while avoiding a nearly
// global first impression on wide desktop displays.
const OISST_INDO_PACIFIC_VIEW = Object.freeze({
    ...INDO_PACIFIC_SST_VIEW,
    zoom: 2.35,
});

// These are deliberately independent portal variables.  A hindcast,
// reanalysis, and operational forecast should never be presented as one
// continuous timeline or silently substituted for one another.
const WAVE_PRODUCTS = {
    '6.3.2.1': { source: 'paccsap', label: 'Historical waves — PACCSAP/CAWCR' },
    '6.3.2.2': { source: 'era5', label: 'Wave reanalysis — ERA5' },
    '6.3.2.3': { source: 'noaa', label: 'Wave conditions & forecast — NOAA WW3' },
};

function getForecastMarks(dates) {
    if (!dates?.length) return () => [];
    return () => dates.map((d, i) => {
        const month = String(d).slice(5, 7);
        if (i === 0 || i === dates.length - 1 || month === '01' || month === '07') {
            return { value: i, label: String(d).slice(0, 7) };
        }
        return null;
    }).filter(Boolean);
}

function buildForecastConfig(model, dateIndex, options = {}) {
    const isCfs = model === 'cfs';
    const varOrder = isCfs ? cfsVariableOrder : gfsVariableOrder;
    const varMeta = isCfs ? cfsVariableMetadata : gfsVariableMetadata;
    const regionMetaAll = isCfs ? cfsRegionMetadata : gfsRegionMetadata;
    const datesByVar = isCfs ? CFS_DATES : GFS_DATES;

    const forecastVar = varOrder.includes(options.forecastVar)
        ? options.forecastVar
        : (varOrder[0] || 'sst');
    const mode = options.forecastMode === 'contour' ? 'contour' : 'gridded';
    const scope = options.forecastViewScope || 'palau';
    const isIndoPacific = scope === 'indo-pacific';
    const isRmiAnalysis = scope === 'rmi' || scope === 'rmi-wake';
    const region = isRmiAnalysis ? scope : 'palau';
    const regionMeta = regionMetaAll[isIndoPacific ? 'indo-pacific' : region] || (
        region === 'rmi-wake'
            ? { overlayUrl: '/CMIPtas/rmiEEZWithWake.json' }
            : {}
    );
    const dateData = datesByVar[forecastVar] || [];
    const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dateData.length - 1, 0)));
    const dateStr = dateData[safeIndex] || dateData[dateData.length - 1] || dateData[0];
    const folderRegion = isIndoPacific ? 'indo' : region;
    const styleFolder = mode === 'contour' ? 'contour' : 'gridded';
    const stem = `${model.toUpperCase()}_${forecastVar.toUpperCase()}_${dateStr}`;
    // Wind is an explicitly spatially queried FlatGeobuf layer.  Naming it
    // directly avoids the JSON fallback path and guarantees bbox requests use
    // the packed FGB R-tree.
    const tileExtension = forecastVar === 'wind' ? 'fgb' : 'json';
    const tilePath = dateStr
        ? `/jsonFiles/forecasts/${model}/${forecastVar}/${folderRegion}/${styleFolder}/${stem}.${tileExtension}`
        : null;

    const localCenter = region === 'rmi-wake'
        ? { latitude: 12.3, longitude: 166.7, zoom: 3.9, bearing: 0, pitch: 0 }
        : (region === 'rmi'
            ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 }
            : VIEW_STATES.SECOND);
    const config = {
        scatterplotVar: isCfs ? 'cfs_forecast' : 'gfs_forecast',
        lat: null,
        lon: null,
        dateData,
        markerData: getForecastMarks(dateData),
        tooltip: (v) => dateData[v.value] || dateData[safeIndex] || '',
        view: isIndoPacific ? INDO_PACIFIC_SST_VIEW : localCenter,
        sliderVisible: true,
        forecastDateIndex: safeIndex,
        forecastVar,
        forecastVarMeta: varMeta[forecastVar],
        crwEnabled: false,
        jsonPaths: [],
        handlerKeys: [],
    };

    if (!tilePath) {
        config.jsonPaths = regionMeta.overlayUrl ? [regionMeta.overlayUrl] : [];
        config.handlerKeys = regionMeta.overlayUrl ? ['eez'] : [];
        return config;
    }

    if (isIndoPacific) {
        // Keep both national EEZs and their island landmasses visible over the
        // basin tile. They are reference overlays, not data layers, so they do
        // not participate in forecast-grid interaction or rendering state.
        config.jsonPaths = [
            tilePath,
            '/CMIPtas/palauEEZ.json',
            '/CMIPtas/rmiEEZ.json',
            '/palau.json',
            '/CMIPtas/rmiLand.json',
        ];
        config.handlerKeys = [
            mode === 'contour' ? null : 'forecast_grid',
            'eez',
            'eez',
            null,
            null,
        ];
    } else if (isRmiAnalysis) {
        config.jsonPaths = [tilePath, regionMeta.overlayUrl, '/CMIPtas/rmiLand.json'];
        config.handlerKeys = mode === 'contour'
            ? ['eez', null, null]
            : ['forecast_grid', 'eez', null];
    } else {
        config.jsonPaths = [tilePath, regionMeta.overlayUrl];
        config.handlerKeys = mode === 'contour' ? ['eez'] : ['forecast_grid', 'eez'];
    }
    return config;
}

// Reconstruct dictionaries locally if not exported from vars

const prec_6mon = allForecasts.prec_6mon;
const sst_6mon = allForecasts.sst_6mon;
const prec6monMarks = createMarksGenerator(prec_6mon);
const sst6monMarks = createMarksGenerator(sst_6mon);

// Maintain a fallback reference to the active CMIP6 variable in case 
// the parent component fails to pass variableSelected down the tree.
let activeCmip6Variable = '7.1.3.1.2';
let activeCmip6Scope = 'palau';
export const getActiveCmip6Variable = () => activeCmip6Variable;
export const getActiveCmip6Scope = () => activeCmip6Scope;

const getOisstDatesForOptions = (options = {}) => {
    const region = options.oisstRegion === 'rmi' ? 'rmi' : 'palau';
    if (options.oisstIndoPacific) return oisstIndoPacificDates;
    return region === 'rmi' ? oisstRmiDates : oisstPalauDates;
};

const applyGhcndPortalScope = (config, options = {}) => {
    const scope = options.portalScope === 'rmi' ? 'rmi' : 'palau';
    const isRmi = scope === 'rmi';
    config.jsonPaths = [
        `/CMIPtas/${isRmi ? 'rmiEEZ' : 'palauEEZ'}.json`,
        `/jsonFiles/ghcnd/stations.json?scope=${scope}`,
    ];
    config.lat = isRmi ? '7.065' : '7.33674';
    config.lon = isRmi ? '171.272' : '134.47686';
    config.view = isRmi
        ? { latitude: 9.5, longitude: 166.5, zoom: 4.15, bearing: 0, pitch: 0 }
        : VIEW_STATES.PALAU_CLOSE;
};

const applyOceanColorConfig = (config, product, dateIndex, options, scale = null) => {
    const scope = options.oceanColorScope === 'rmi' ? 'rmi' : 'palau';
    const renderMode = options.oceanColorMode === 'contour' ? 'contour' : 'gridded';
    const dates = oceanColorDates?.[scope]?.[product] || [];
    const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(0, dates.length - 1)));
    const date = dates[safeIndex] || null;
    const scopeName = scope === 'rmi' ? 'Marshall Islands' : 'Palau';
    const productPath = scale ? `${product}/${scale}` : product;
    const plotProduct = scale ? `${product}_${scale}` : product;
    config.scatterplotVar = `ocean_color:${plotProduct}:${scope}`;
    config.view = scope === 'rmi'
        ? { latitude: 9.9, longitude: 166.5, zoom: 4.1, bearing: 0, pitch: 0 }
        : VIEW_STATES.SECOND;
    config.jsonPaths = date
        ? [
            `/jsonFiles/ocean-color/${scope}/${productPath}/${renderMode}/${date}.fgb`,
            `/CMIPtas/${scope === 'rmi' ? 'rmiEEZ' : 'palauEEZ'}.json`,
            scope === 'rmi' ? '/CMIPtas/rmiLand.json' : '/palau.json',
        ]
        : [`/CMIPtas/${scope === 'rmi' ? 'rmiEEZ' : 'palauEEZ'}.json`];
    config.handlerKeys = date
        ? [renderMode === 'contour' ? null : 'ocean_color', 'eez', null]
        : ['eez'];
    config.dateData = dates;
    config.markerData = dates.map((value, index) => {
        if (index === 0 || index === dates.length - 1 || index % Math.max(1, Math.floor(dates.length / 5)) === 0) {
            return { value: index, label: value.slice(0, 7) };
        }
        return null;
    }).filter(Boolean);
    config.tooltip = ({ value }) => dates[value] || date || '';
    config.sliderVisible = dates.length > 0;
    config.oceanColorDateIndex = safeIndex;
    config.oceanColorRenderMode = renderMode;
    config.oceanColorScale = scale;
    config.oceanColorScopeLabel = scopeName;
    config.colorbar = null;
    return config;
};

/** Year ticks at real date indices (same pattern as MHW monthly marks). */
const getOisstMarks = (dates) => {
    const marks = dates.map((date, index) => {
        if (date.endsWith('-01-01') && parseInt(date.split('-')[0], 10) % 5 === 0) {
            return { value: index, label: date.slice(0, 4) };
        }
        return null;
    }).filter(Boolean);
    return () => marks;
};

function buildApccConfig(variable, dateIndex, options = {}) {
    const parsed = parseApccId(variable);
    const variableMeta = APCC_VARIABLES[parsed.variable];
    const aggregation = options.apccAggregation === 'seasonal' ? 'seasonal' : 'monthly';
    const requestedScope = options.apccViewScope || 'palau';
    const scope = variableMeta.local ? requestedScope : 'indo-pacific';
    const mode = ['gridded', 'voronoi', 'contour'].includes(options.apccMode)
        ? options.apccMode
        : 'gridded';
    const records = getApccRecords(parsed, aggregation, scope, mode);
    const dateData = [...new Set(records.map(record => record.valid))].sort();
    const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dateData.length - 1, 0)));
    const date = dateData[safeIndex];
    const record = records.find(item => item.valid === date) || records[0];
    const isIndoPacific = scope === 'indo-pacific';
    const isRmi = scope === 'rmi';
    const overlays = isIndoPacific
        ? ['/CMIPtas/palauEEZ.json', '/CMIPtas/rmiEEZ.json', '/palau.json', '/CMIPtas/rmiLand.json']
        : (isRmi ? ['/CMIPtas/rmiEEZ.json', '/CMIPtas/rmiLand.json'] : ['/CMIPtas/palauEEZ.json', '/palau.json']);
    const dataHandler = mode === 'contour' ? null : 'forecast_grid';
    return {
        scatterplotVar: 'apcc_mme',
        lat: null,
        lon: null,
        dateData,
        markerData: getForecastMarks(dateData),
        tooltip: ({ value }) => dateData[value] || date || '',
        view: isIndoPacific
            ? { latitude: 5, longitude: 205, zoom: 2.05, bearing: 0, pitch: 0 }
            : (isRmi ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 } : VIEW_STATES.SECOND),
        sliderVisible: dateData.length > 1,
        apccDateIndex: safeIndex,
        apccParsed: parsed,
        apccVariableMeta: variableMeta,
        crwEnabled: false,
        jsonPaths: record ? [record.path, ...overlays] : overlays,
        handlerKeys: record ? [dataHandler, ...overlays.map(() => null)] : overlays.map(() => null),
    };
}


export const getVariableConfig = (variable, dateIndex, options = {}) => {
    // Default Configuration
    let config = {
        handlerKeys: ['eez'],
        currentDial: null,
        lat: "6.933517932891846",
        lon: "133.60003662109375",
        scatterplotVar: "home",
        variableSelected: variable,
        jsonPaths: [`/CMIPtas/palauEEZ.json`],
        dateData: YEARS,
        markerData: getYearsMarks,
        tooltip: defaultToolTip,
        colorbar: null,
        crwEnabled: false,
        sliderVisible: true,
        view: VIEW_STATES.SECOND
    };

    if (!variable) return config;

    // --- APCC MME ---
    if (parseApccId(variable)) {
        Object.assign(config, buildApccConfig(variable, dateIndex, options));
    }
    // --- Atmosphere ---
    else if (variable === "2.1.1.3") { // Regional Mean Temp Hist
        config.scatterplotVar = "mst";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "2.2.1.3") { // Hot Days Hist
        config.scatterplotVar = "hotdays";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "2.3.1.3") { // Cold Nights Hist
        config.scatterplotVar = "colddays";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "3.1.1.3") { // Wet Rainfall Hist
        config.scatterplotVar = "wetrainfall";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "3.2.1.3") { // Cons Dry Days Hist
        config.scatterplotVar = "consdry";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "3.3.1.3") { // Heavy Rainfall
        config.scatterplotVar = "heavyrainfall";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "3.4.1.3") { // Wet Days Hist
        config.scatterplotVar = "wetdays";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "3.5.1.3") { // Dry Days
        config.scatterplotVar = "drydays";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`, 'jsonFiles/ghcnd/stations.json'];
        config.handlerKeys = ['eez', 'station'];
        config.lat = "7.33674"; config.lon = "134.47686";
        config.view = VIEW_STATES.PALAU_CLOSE;
        config.sliderVisible = true;
        applyGhcndPortalScope(config, options);
    }
    else if (variable === "3.6.2") { // APCC 6M Precip Forecast
        config.scatterplotVar = "APCC-PRECIP";
        const precipDate = prec_6mon[dateIndex] || prec_6mon[0];
        
        if (precipDate) {
            config.jsonPaths = [`/MME-HRES/FORECAST/6-MON/PREC/PREC_GAUS_${precipDate}.json`, `/CMIPtas/palauEEZ.json`];
            config.handlerKeys = [null, 'eez']; 
            config.colorbar = null; // Placeholder for image path if available
        } else {
            config.jsonPaths = [`/CMIPtas/palauEEZ.json`];
        }
        config.lat = "7.33674";
        config.lon = "134.47686";
        config.dateData = prec_6mon;
        config.markerData = () => prec6monMarks;
        config.tooltip = (v) => `${v.value}`;
        config.view = VIEW_STATES.SECOND;
        config.sliderVisible = true;
    }
    // --- Ocean ---
    else if (variable === "6.1.1.3") { // Regional Sea Level Hist
        const scope = ['palau', 'rmi', 'indo'].includes(options.regionalSeaLevelScope)
            ? options.regionalSeaLevelScope
            : 'palau';
        const mode = options.regionalSeaLevelMode === 'contour' ? 'contour' : 'gridded';
        const dates = regionalSeaLevelDates[scope] || [];
        const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(0, dates.length - 1)));
        const stamp = dates[safeIndex] || dates[dates.length - 1];
        const metadata = regionalSeaLevelMetadata[scope] || {};
        const eezPath = scope === 'rmi'
            ? '/CMIPtas/rmiEEZ.json'
            : (scope === 'palau' ? '/CMIPtas/palauEEZ.json' : null);
        const dataPath = stamp
            ? `/jsonFiles/regional-sea-level/${scope}/${mode}/sla_${stamp}.fgb`
            : null;
        // A scope is selectable before its first bootstrap publication. Avoid
        // asking the browser for a station file that cannot exist yet; the
        // publisher adds dates and metadata atomically once the scope is ready.
        const stationPath = scope === 'indo' || dates.length === 0
            ? null
            : `/jsonFiles/regional-sea-level/${scope}/stations.json`;
        // A country scope with a tide gauge opens as the scientifically useful
        // station-versus-nearest-altimetry comparison. Basin/no-gauge scopes
        // retain the EEZ/basin aggregate fallback.
        const defaultStation = metadata.stations?.[0];
        config.scatterplotVar = defaultStation
            ? `regional_sea_level:station:${scope}:${defaultStation.id}`
            : `regional_sea_level:summary:${scope}`;
        config.jsonPaths = [];
        config.handlerKeys = [];
        if (dataPath) {
            config.jsonPaths.push(dataPath);
            config.handlerKeys.push(mode === 'gridded' ? 'regional_sea_level' : null);
        }
        if (eezPath) {
            config.jsonPaths.push(eezPath);
            config.handlerKeys.push(null);
        }
        if (stationPath) {
            config.jsonPaths.push(stationPath);
            config.handlerKeys.push('sea_level_station');
        }
        config.colorbar = null;
        config.dateData = dates;
        config.markerData = () => dates.map((value, index) => ({
            value: index,
            label: index === 0 || index === dates.length - 1 || String(value).endsWith('-01')
                ? String(value).slice(0, 4)
                : '',
        }));
        config.tooltip = { open: true, formatter: value => dates[value] || '' };
        config.lat = String(metadata.view?.latitude ?? (scope === 'rmi' ? 9.9 : scope === 'indo' ? 5 : 7.34));
        config.lon = String(metadata.view?.longitude ?? (scope === 'rmi' ? 166.5 : scope === 'indo' ? 205 : 134.48));
        config.view = metadata.view
            ? { ...metadata.view, bearing: 0, pitch: 0 }
            : (scope === 'rmi'
                ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 }
                : (scope === 'indo' ? { latitude: 5, longitude: 205, zoom: 2.05, bearing: 0, pitch: 0 } : VIEW_STATES.PALAU_STATION));
        config.sliderVisible = true;
    }
    else if (variable === "6.2.2.3") { // Flood Analysis
        config.scatterplotVar = "flood_analysis";
        config.sliderVisible = false;
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`,'/jsonFiles/tidestation.json'];
        config.handlerKeys = ['eez','station'];
    }
    else if (variable === "6.2.3.3") { // Flood Duration
        config.scatterplotVar = "flood_duration";
        config.sliderVisible = false;
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`,'/jsonFiles/tidestation.json'];
        config.handlerKeys = ['eez','station'];
    }
    else if (WAVE_PRODUCTS[variable]) {
        config.scatterplotVar = "wave";
        const waveProduct = WAVE_PRODUCTS[variable];
        const waveScope = options.waveViewScope === 'rmi' ? 'rmi' : 'palau';
        const waveSource = waveProduct.source;
        const waveRenderMode = options.waveRenderMode === 'gridded' ? 'gridded' : 'contour';
        const continuumDates = waveContinuumTimeline[waveScope]?.[waveSource] || [];
        const useContinuum = continuumDates.length > 0;
        // Retain the already-published PACCSAP archive while its new
        // source-explicit publisher backfills history.  ERA5 and NOAA WW3
        // intentionally have no fallback because their dates are distinct.
        const dates = useContinuum
            ? continuumDates
            : (waveSource === 'paccsap' && waveHindcastDates[waveScope]?.length
                ? waveHindcastDates[waveScope]
                : (waveSource === 'paccsap' && waveScope === 'palau' ? waveYEARS : []));
        const waveDateStr = dates[dateIndex] || dates[dates.length - 1];
        const legacyPalau = waveSource === 'paccsap' && !useContinuum && waveScope === 'palau'
            && waveHindcastMetadata.palau?.source === 'legacy';
        const eezPath = waveScope === 'rmi' ? '/CMIPtas/rmiEEZ.json' : '/CMIPtas/palauEEZ.json';
        config.jsonPaths = waveDateStr
            ? [
                eezPath,
                useContinuum
                    ? `/jsonFiles/wave-continuum/${waveScope}/${waveSource}/${waveRenderMode}/wave${waveRenderMode === 'gridded' ? 'Grid' : 'Contour'}${waveDateStr}.fgb`
                    : (legacyPalau
                    ? `/jsonFiles/waveContour/waveContour${waveDateStr}.fgb`
                    : (waveRenderMode === 'gridded'
                        ? `/jsonFiles/wave-hindcast/${waveScope}/gridded/waveGrid${waveDateStr}.fgb`
                        : `/jsonFiles/wave-hindcast/${waveScope}/contour/waveContour${waveDateStr}.fgb`)),
                useContinuum
                    ? `/jsonFiles/wave-continuum/${waveScope}/${waveSource}/arrow/waveArrow${waveDateStr}.json`
                    : (legacyPalau
                    ? `/jsonFiles/waveArrow/waveArrow${waveDateStr}.json`
                    : `/jsonFiles/wave-hindcast/${waveScope}/arrow/waveArrow${waveDateStr}.json`),
            ]
            : [eezPath];
        config.handlerKeys = waveDateStr ? ['eez', 'wave_grid', 'arrow'] : ['eez'];
        // The existing side-chart archive is PACCSAP-only. Do not show it for
        // ERA5 or NOAA WW3—doing so would falsely present hindcast values as
        // reanalysis or forecast data. Their own time-series files will be
        // wired in alongside their source-specific publication manifests.
        config.scatterplotVar = waveSource === 'paccsap' && dates.length ? "wave" : null;
        config.dateData = dates;
        config.waveProduct = waveProduct;
        config.markerData = () => dates.map((value, index) => ({
            value: index,
            // Daily model archives become unreadable with every date shown.
            // Preserve the first/last day and a weekly cadence.
            label: index === 0 || index === dates.length - 1 || index % 7 === 0 ? value : '',
        }));
        config.tooltip = { open: true, formatter: value => dates[value] || '' };
        config.sliderVisible = dates.length > 0;
        config.view = waveScope === 'rmi'
            ? { latitude: 9.5, longitude: 166.5, zoom: 4.7, bearing: 0, pitch: 0 }
            : VIEW_STATES.INITAL;
    }
    else if (variable === "7.1.2.1") { // Mean SST Annual
        config.scatterplotVar = "oisst";
        config.jsonPaths = [`/CMIPtas/palauEEZ.json`]; 
        config.handlerKeys = ['eez'];
        config.dateData = waveYEARS; // Placeholder
        config.markerData = getWaveYearsMarks;
        config.tooltip = waveToolTip;
        config.view = VIEW_STATES.SECOND;
        config.sliderVisible = true;
    }
    else if (variable === "7.1.2.2") { // Monthly OISST
        config.scatterplotVar = "oisst_monthly";
        config.lat = null;
        config.lon = null;
        const region = options.oisstRegion === 'rmi' ? 'rmi' : 'palau';
        const isIndoPacific = Boolean(options.oisstIndoPacific);
        const dateData = getOisstDatesForOptions(options);
        const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dateData.length - 1, 0)));
        const dateStr = dateData[safeIndex] || dateData[dateData.length - 1] || dateData[0];
        const mode = options.oisstMode || 'gridded';
        const regionMeta = oisstRegionMetadata[region];
        const localCenter = region === 'rmi'
            ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 }
            : VIEW_STATES.SECOND;
        const griddedFolder = isIndoPacific
            ? '/jsonFiles/OISSTMonthlyIndoPacific'
            : (region === 'rmi' ? '/jsonFiles/OISSTMonthly_RMI' : '/jsonFiles/OISSTMonthly');
        const contourFolder = isIndoPacific
            ? '/jsonFiles/OISSTMonthlyContourIndoPacific'
            : (region === 'rmi' ? '/jsonFiles/OISSTMonthlyContour_RMI' : '/jsonFiles/OISSTMonthlyContour');

        const sstPath = mode === 'contour'
            ? `${contourFolder}/OISST_MONTH_CONTOUR_${dateStr}.json`
            : `${griddedFolder}/OISST_MONTH_${dateStr}.json`;

        if (isIndoPacific) {
            // Basin view only — local EEZ overlay is tiny and handler length must match paths.
            config.jsonPaths = [sstPath];
            config.handlerKeys = [mode === 'contour' ? null : 'oisst_grid'];
        } else if (region === 'rmi') {
            // SST + EEZ outline + shoreline fill (matches oisst-ENSO-RMI land masking visuals).
            config.jsonPaths = [sstPath, regionMeta.overlayUrl, '/CMIPtas/rmiLand.json'];
            config.handlerKeys = mode === 'contour'
                ? ['eez', null, null]
                : ['oisst_grid', 'eez', null];
        } else {
            config.jsonPaths = [sstPath, regionMeta.overlayUrl];
            config.handlerKeys = mode === 'contour' ? ['eez'] : ['oisst_grid', 'eez'];
        }

        config.dateData = dateData;
        config.markerData = getOisstMarks(dateData);
        config.tooltip = (v) => dateData[v.value] || dateData[safeIndex] || '';
        config.view = isIndoPacific ? OISST_INDO_PACIFIC_VIEW : localCenter;
        config.sliderVisible = true;
        config.oisstDateIndex = safeIndex;
    }
    else if (isEkmanVariable(variable)) {
        // Ekman tuna-migration tool — always Indo-Pacific, always gridded FGB tiles
        const ekmanModel = variable === 'ekman.cfs' ? 'cfs' : variable === 'ekman.gfs' ? 'gfs' : 'metop';
        const dateData =
            ekmanModel === 'cfs'   ? ekmanCfsDates  :
            ekmanModel === 'gfs'   ? ekmanGfsDates  :
                                     ekmanMetopDates;
        const meta = ekmanVariableMetadata[ekmanModel] || ekmanVariableMetadata.cfs;
        const regionMeta = ekmanRegionMetadata['indo-pacific'];
        const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dateData.length - 1, 0)));
        const stem = dateData[safeIndex] || dateData[dateData.length - 1] || '';
        const baseDir = ekmanModel === 'cfs' ? regionMeta.cfsDir
                      : ekmanModel === 'gfs' ? regionMeta.gfsDir
                      : regionMeta.metopDir;
        const tilePath = stem ? `${baseDir}/${stem}.fgb` : null;
        const marks = () => dateData.map((d, i) => {
            // Label every 12th entry (roughly annual ticks)
            if (i === 0 || i % 12 === 0) return { value: i, label: d.slice(-7) };
            return null;
        }).filter(Boolean);

        config.scatterplotVar = 'ekman';
        config.lat = null;
        config.lon = null;
        config.dateData = dateData;
        config.markerData = marks;
        config.tooltip = (v) => dateData[v.value] || '';
        config.view = INDO_PACIFIC_SST_VIEW;
        config.sliderVisible = true;
        config.forecastDateIndex = safeIndex;
        config.forecastVar = 'ekman';
        config.forecastVarMeta = meta;
        config.ekmanModel = ekmanModel;
        config.crwEnabled = false;
        config.jsonPaths = tilePath ? [tilePath] : [];
        config.handlerKeys = tilePath ? ['ekman_grid'] : [];
    }
    else if (variable === 'hurricane.tracker') {
        // Hurricane layers are composed in App.js; GFS wind supplies the
        // synchronized contour and animated particle field underneath them.
        const first = hurricaneActiveStorms[0];
        const gfsWind = buildForecastConfig('gfs', dateIndex, {
            forecastVar: 'wind', forecastMode: 'gridded', forecastViewScope: 'indo-pacific',
        });
        Object.assign(config, gfsWind);
        const dates = GFS_DATES.wind || [];
        const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dates.length - 1, 0)));
        const dateStr = dates[safeIndex];
        const base = dateStr ? `/jsonFiles/forecasts/gfs/wind/indo` : null;
        config.scatterplotVar = 'hurricane';
        config.sliderVisible = Boolean(dateStr);
        config.crwEnabled = false;
        config.forecastDateIndex = safeIndex;
        config.dateData = dates;
        config.crwDateIndex = safeIndex;
        config.markerData = getForecastMarks(dates);
        config.tooltip = (v) => dates[v.value] || dates[safeIndex] || '';
        // Use the identical GFS gridded product as Atmosphere → Wind. It
        // contains the U/V components used by particles; do not stack the
        // contour product here because that changes the wind appearance.
        config.jsonPaths = base ? [
            `${base}/gridded/GFS_WIND_${dateStr}.fgb`,
            '/CMIPtas/palauEEZ.json', '/CMIPtas/rmiEEZ.json',
            '/palau.json', '/CMIPtas/rmiLand.json',
        ] : [];
        config.handlerKeys = base ? ['forecast_grid', 'eez', 'eez', null, null] : [];
        config.lat = null;
        config.lon = null;
        if (first) {
            config.view = {
                latitude: first.lat,
                longitude: first.lon,
                zoom: 4,
                bearing: 0,
                pitch: 0,
            };
        } else {
            // Basin-scale Pacific default (tuna / Palau-relevant view)
            config.view = { latitude: 15, longitude: 180, zoom: 2.2, bearing: 0, pitch: 0 };
        }
    }
    else if (variable === "7.1.5" || variable === "7.1.6" || parseForecastId(variable)) {
        // CFS / GFS forecast leaves: cfs.sst, gfs.wind, legacy 7.1.5 / 7.1.6, …
        const parsed = parseForecastId(variable) || (
            variable === '7.1.6'
                ? { model: 'gfs', varKey: 'sst' }
                : { model: 'cfs', varKey: 'sst' }
        );
        Object.assign(config, buildForecastConfig(parsed.model, dateIndex, {
            ...options,
            forecastVar: parsed.varKey,
        }));
    }
    else if (variable === "2.4.2" || variable === "7.1.4.2") { // APCC 6M SST Forecast (Handling both keys)
        config.scatterplotVar = "APCC-SST";
        const sstDate = sst_6mon[dateIndex] || sst_6mon[0];
        
        if (sstDate) {
            config.jsonPaths = [`/MME-HRES/FORECAST/6-MON/SST/SST_GAUS_${sstDate}.json`, `/CMIPtas/palauEEZ.json`];
            config.handlerKeys = ['tos', 'eez'];
            config.colorbar = null; 
        } else {
            config.jsonPaths = [`/CMIPtas/palauEEZ.json`];
        }
        config.lat = "7.33674";
        config.lon = "134.47686";
        config.dateData = sst_6mon;
        config.markerData = () => sst6monMarks;
        config.tooltip = tosHistoricToolTip;
        config.view = VIEW_STATES.SECOND;
        config.sliderVisible = true;
    }
    // The CMIP7 catalogue is an availability explorer, not a processed map
    // product. Keep the map as quiet geographic context and never route the
    // catalogue selection through the CMIP6 projection renderer.
    else if (variable === "cmip7.catalogue") {
        const scope = ['palau', 'rmi', 'rmi-wake', 'indo'].includes(options.cmip6Scope)
            ? options.cmip6Scope
            : 'palau';
        const eez = scope === 'rmi-wake'
            ? '/CMIPtas/rmiEEZWithWake.fgb'
            : (scope === 'rmi'
                ? '/CMIPtas/rmiEEZ.fgb'
                : (scope === 'palau' ? '/CMIPtas/palauEEZ.fgb' : null));
        config.scatterplotVar = null;
        config.lat = null;
        config.lon = null;
        config.dateData = [];
        config.markerData = () => [];
        config.jsonPaths = eez ? [eez] : [];
        config.handlerKeys = eez ? ['eez'] : [];
        config.view = scope === 'indo'
            ? INDO_PACIFIC_SST_VIEW
            : (scope === 'rmi-wake'
                ? { latitude: 12.3, longitude: 166.7, zoom: 3.9, bearing: 0, pitch: 0 }
                : (scope === 'rmi'
                    ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 }
                    : VIEW_STATES.SECOND));
        config.sliderVisible = false;
        config.colorbar = null;
        config.cmip7 = { catalogue: true, scope };
    }
    // --- Manifest-driven CMIP6 and CMIP7 projection products ---
    else if (variable.startsWith("7.1.3") || variable.startsWith("cmip6.") || variable.startsWith("cmip7.")) {
        activeCmip6Variable = variable;
        config.scatterplotVar = "CMIP6";
        config.lat = null;
        config.lon = null;

        // New CMIP6 entries use ``cmip6.<variable>.<scenario>``. Keeping the
        // earlier SST IDs working lets us introduce variables as their
        // manifest products arrive without a risky menu migration.
        const manifestBackedProjection = variable.startsWith('cmip6.') || variable.startsWith('cmip7.');
        const cmip7Preview = variable.startsWith('cmip7.');
        const [, requestedVariable = 'tos', requestedScenario = 'ssp126'] = manifestBackedProjection
            ? variable.split('.')
            : [];

        // New publications are self-describing.  Keep the legacy files only as
        // a migration fallback while a real manifest has not been published.
        const manifest = options.cmip6Catalog;
        const manifestScenario = manifestBackedProjection ? requestedScenario : ({
            '7.1.3.1.2': 'ssp126', '7.1.3.1.5': 'ssp434',
            '7.1.3.1.6': 'ssp460', '7.1.3.1.7': 'ssp534-over',
            '7.1.3.1.8': 'ssp585', '7.1.3.3': 'historical',
        }[variable] || 'ssp126');
        const cmip6Variable = manifestBackedProjection ? requestedVariable : 'tos';
        const variableManifest = manifest?.variables?.[cmip6Variable];
        if (variableManifest?.products?.length) {
            const scope = ['palau', 'rmi', 'rmi-wake', 'indo'].includes(options.cmip6Scope) ? options.cmip6Scope : 'palau';
            activeCmip6Scope = scope;
            const requestedMode = options.cmip6RenderMode || 'native';
            const scopeProducts = variableManifest.products.filter(product => (
                product.scenario === manifestScenario && product.scope === scope
            ));
            const availableModes = [...new Set(scopeProducts.flatMap(product => Object.keys(product.modes || {})))];
            const mode = availableModes.includes(requestedMode)
                ? requestedMode
                : (scope === 'indo' && availableModes.includes('standard-grid')
                    ? 'standard-grid'
                    : (['native', 'eez-grid', 'standard-grid', 'contour'].find(candidate => availableModes.includes(candidate)) || requestedMode));
            const candidates = scopeProducts.filter(product => product.modes?.[mode]);
            const requestedModel = options.cmip6Model;
            const selected = candidates.find(product => `${product.source?.source_id}:${product.source?.variant_label}:${product.source?.grid_label}` === requestedModel) || candidates[0];
            if (selected) {
                const years = [...new Set(candidates.map(product => Number(product.year)).filter(Number.isFinite))].sort((a, b) => a - b);
                const year = years[Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(0, years.length - 1)))];
                const frame = candidates.find(product => Number(product.year) === year && product.source?.source_id === selected.source?.source_id && product.modes?.[mode]) || selected;
                const eez = scope === 'rmi-wake'
                    ? '/CMIPtas/rmiEEZWithWake.fgb'
                    : (scope === 'rmi' ? '/CMIPtas/rmiEEZ.fgb' : (scope === 'palau' ? '/CMIPtas/palauEEZ.fgb' : null));
                config.dateData = years;
                config.markerData = () => years.map((value, index) => (
                    index === 0 || index === years.length - 1 || value % 10 === 0 ? { value: index, label: String(value) } : null
                )).filter(Boolean);
                config.tooltip = ({ value }) => String(years[value] || year);
                config.jsonPaths = [frame.modes[mode], ...(eez ? [eez] : [])];
                // Every manifest-backed CMIP6 native/grid layer exposes the
                // same stable ``lat`` / ``lon`` feature properties. Reuse the
                // location handler for all variables so a newly published
                // variable is immediately inspectable, not map-only.
                config.handlerKeys = [mode === 'contour' ? null : 'tos', ...(eez ? ['eez'] : [])];
                config.colorbar = null;
                config.view = scope === 'rmi-wake'
                    ? { latitude: 12.3, longitude: 166.7, zoom: 3.9, bearing: 0, pitch: 0 }
                    : (scope === 'rmi'
                    ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 }
                    : (scope === 'indo' ? INDO_PACIFIC_SST_VIEW : VIEW_STATES.SECOND));
                config.sliderVisible = years.length > 1;
                config.cmip6 = { variable: cmip6Variable, label: variableManifest.label || cmip6Variable, scope, mode, scenario: manifestScenario, selectedModel: frame.source, availableModels: candidates.map(product => product.source), availableModes, legend: variableManifest.legend, previewPhase: cmip7Preview ? 'CMIP7' : null };
                return config;
            }
        }

        // A requested CMIP6 variable can legitimately have no selected ESGF
        // dataset yet. Show its geographic context and availability message,
        // never the legacy SST layer under a different scientific label.
        if (manifestBackedProjection) {
            const scope = ['palau', 'rmi', 'rmi-wake', 'indo'].includes(options.cmip6Scope) ? options.cmip6Scope : 'palau';
            activeCmip6Scope = scope;
            const eez = scope === 'rmi-wake'
                ? '/CMIPtas/rmiEEZWithWake.fgb'
                : (scope === 'rmi' ? '/CMIPtas/rmiEEZ.fgb' : (scope === 'palau' ? '/CMIPtas/palauEEZ.fgb' : null));
            config.jsonPaths = eez ? [eez] : [];
            config.handlerKeys = eez ? ['eez'] : [];
            config.dateData = [];
            config.markerData = () => [];
            config.tooltip = () => cmip7Preview ? 'CMIP7 data pending publication' : 'CMIP6 data not published yet';
            config.sliderVisible = false;
            config.view = scope === 'rmi-wake'
                ? { latitude: 12.3, longitude: 166.7, zoom: 3.9, bearing: 0, pitch: 0 }
                : (scope === 'rmi' ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 } : (scope === 'indo' ? INDO_PACIFIC_SST_VIEW : VIEW_STATES.SECOND));
            config.cmip6 = { variable: cmip6Variable, label: variableManifest?.label || cmip6Variable, scope, scenario: manifestScenario, unavailable: true, legend: variableManifest?.legend || null, previewPhase: cmip7Preview ? 'CMIP7' : null };
            return config;
        }
        
        let ssp = 'ssp126';
        if (variable === "7.1.3.1.2") { 
            ssp = 'ssp126'; 
            config.dateData = tosSSP126Years; 
            config.markerData = gettosSSP126Marks; 
            config.tooltip = tosSSP126ToolTip; 
            config.colorbar = "/colorbar/tos_Omon_HadGEM3-GC31-MM_ssp126_r1i1p1f3/2015-2100_colorbar_tos_Omon_HadGEM3-GC31-MM_ssp126_r1i1p1f3.png";
        } else if (variable === "7.1.3.1.5") {
            ssp = 'ssp434';
            config.dateData = tosSSP434Years;
            config.markerData = gettosSSP434Marks;
            config.tooltip = tosSSP434ToolTip;
        } else if (variable === "7.1.3.1.6") {
            ssp = 'ssp460';
            config.dateData = tosSSP460Years;
            config.markerData = gettosSSP460Marks;
            config.tooltip = tosSSP460ToolTip;
        } else if (variable === "7.1.3.1.7") {
            ssp = 'ssp534-over';
            config.dateData = tosSSP534OVERYears;
            config.markerData = gettosSSP534OVERMarks;
            config.tooltip = tosSSP534OVERToolTip;
        } else if (variable === "7.1.3.1.8") {
            ssp = 'ssp585';
            config.dateData = tosSSP585Years;
            config.markerData = gettosSSP585Marks;
            config.tooltip = tosSSP585ToolTip;
            config.colorbar = '/cmip6_colorbars/tos_ssp585_colorbar.png';
        } else if (variable === "7.1.3.3") {
            ssp = 'historical';
            config.dateData = tosHistoricYears;
            config.markerData = gettosHistoricMarks;
            config.tooltip = tosHistoricToolTip;
        }

        // Dynamically pull the correct year string based on the specific model's available dates
        const yearList = config.dateData;
        const year = yearList[dateIndex >= 0 && dateIndex < yearList.length ? dateIndex : 0];

        let model;
        if (ssp === 'ssp585') {
            model = 'tos_Omon_HadGEM3-GC31-MM_ssp585_r3i1p1f3';
            config.jsonPaths = [`/jsonFiles/tosgeojson/${model}/${year}_${model}.fgb`, `/CMIPtas/palauEEZ.fgb`];
        } else if (ssp === 'ssp434') {
            model = 'tos_Omon_CanESM5_ssp434_r3i1p1f1';
            config.jsonPaths = [`/jsonFiles/tosgeojson/${model}/${year}_${model}.fgb`, `/CMIPtas/palauEEZ.fgb`];
        } else if (ssp === 'ssp126'){
            model = 'tos_Omon_HadGEM3-GC31-MM_ssp126_r1i1p1f3';
            config.jsonPaths = [`/jsonFiles/tosgeojson/${model}/${year}_${model}.fgb`, `/CMIPtas/palauEEZ.fgb`];
        } else if (ssp === 'ssp460'){
            model = 'tos_Omon_CanESM5_ssp460_r4i1p1f1';
            config.jsonPaths = [`/jsonFiles/tosgeojson/${model}/${year}_${model}.fgb`, `/CMIPtas/palauEEZ.fgb`];
        } else if (ssp === 'ssp534-over'){
            model = 'tos_Omon_CanESM5_ssp534-over_r3i1p1f1';
            config.jsonPaths = [`/jsonFiles/tosgeojson/${model}/${year}_${model}.fgb`, `/CMIPtas/palauEEZ.fgb`];
        } else if (ssp === 'historical'){
            model = 'tos_Omon_HadGEM3-GC31-MM_historical_r2i1p1f3';
            config.jsonPaths = [`/jsonFiles/tosgeojson/${model}/${year}_${model}.fgb`, `/CMIPtas/palauEEZ.fgb`];
        } 
        config.handlerKeys = ['tos', 'eez'];
        config.view = VIEW_STATES.SECOND;
        config.sliderVisible = true;
    }
    // --- Coral Reef Watch daily products ---
    else if (variable.startsWith("7.4.4.")) {
        const productBySuffix = {
            '1': 'dhw', '2': 'hotspot', '3': 'coraltemp', '4': 'sst_anomaly', '5': 'sst_trend_7d', '6': 'baa',
        };
        const product = productBySuffix[variable.split('.').pop()];
        config.scatterplotVar = 'crw_daily';
        config.crwEnabled = false;
        config.colorbar = null;
        const scope = ['palau', 'rmi', 'pacific'].includes(options.crwScope) ? options.crwScope : (options.crwPacific ? 'pacific' : 'palau');
        const usePacific = scope === 'pacific';
        const requestedResolution = ['native', '0.25deg', '0.5deg', '1deg', '2deg'].includes(options.crwRes) ? options.crwRes : 'native';
        // Native 0.05° CRW is intentionally limited to the Palau and RMI
        // clips. Pacific native data remains a direct backend download.
        const resolution = usePacific && requestedResolution === 'native' ? '0.25deg' : requestedResolution;
        const availableDates = crwDailyDateCatalog?.[scope]?.[resolution]?.[product] || [];
        const fallbackDate = crwDailyDates[product];
        const dates = availableDates.length ? availableDates : (fallbackDate ? [fallbackDate] : []);
        const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dates.length - 1, 0)));
        const date = dates[safeIndex] || dates.at(-1);
        config.dateData = dates;
        config.markerData = () => dates.map((value, index) => ({ value: index, label: value }));
        config.tooltip = ({ value }) => dates[value] || date || '';
        config.view = usePacific
            ? { latitude: 5, longitude: 205, zoom: 2.05, bearing: 0, pitch: 0 }
            : (scope === 'rmi' ? { latitude: 9.9, longitude: 166.5, zoom: 4.3, bearing: 0, pitch: 0 } : VIEW_STATES.SECOND);
        config.sliderVisible = dates.length > 0;
        // The daily CRW grid is the only interactive layer here. EEZ/land
        // context must not consume its clicks before the selected tile can
        // open the 0.25° history chart.
        config.handlerKeys = usePacific ? ['crw', null, null, null, null] : ['crw', null];
        config.jsonPaths = date
            ? (usePacific
                ? [`/jsonFiles/coral-reef-watch/pacific/${resolution}/${product}/${date}.fgb`, '/CMIPtas/palauEEZ.json', '/CMIPtas/rmiEEZ.json', '/palau.json', '/CMIPtas/rmiLand.json']
                : [`/jsonFiles/coral-reef-watch/${scope}/${resolution}/${product}/${date}.fgb`, scope === 'rmi' ? '/CMIPtas/rmiEEZ.json' : '/CMIPtas/palauEEZ.json'])
            : [`/CMIPtas/palauEEZ.json`];
    }
    // --- Coral Bleaching ---
    else if (variable.startsWith("7.4.3")) {
        config.scatterplotVar = "CRW";
        config.crwEnabled = true;
        config.colorbar = null;
        config.view = VIEW_STATES.SECOND;
        config.sliderVisible = true;

        const isHistorical = variable.startsWith("7.4.3.3");
        const fileNames = isHistorical ? getHistoricCRWNames() : getCurrentCRWNames();
        const fileName = (fileNames && fileNames[dateIndex]) ? fileNames[dateIndex] : (fileNames && fileNames[0] ? fileNames[0] : null);
        
        config.dateData = isHistorical ? historicCRWYears : currentCRWYears;
        config.markerData = isHistorical ? getHistoricCRWMarks : getCurrentCRWMarks;
        config.tooltip = isHistorical ? historicCRWToolTip : currentCRWToolTip;

        let outlookTypePath = '';
        let dialKey = '2';
        const suffix = variable.split('.').pop(); 
        
        if (suffix === '1') { outlookTypePath = 'CRW_BAA_Current_Rounded'; dialKey = '2'; }
        else if (suffix === '2') { outlookTypePath = 'CRW_BAA_Week_01-04'; dialKey = '3'; }
        else if (suffix === '3') { outlookTypePath = 'CRW_BAA_Week_05-08'; dialKey = '4'; }
        else if (suffix === '4') { outlookTypePath = 'CRW_BAA_Week_09-12'; dialKey = '5'; }

        // Opt-in Pacific-wide view is only available for the three outlook dials
        // (the "current" dial is Palau-box/point specific).
        const pacificDials = ['CRW_BAA_Week_01-04', 'CRW_BAA_Week_05-08', 'CRW_BAA_Week_09-12'];
        const usePacific = !!options.crwPacific && pacificDials.includes(outlookTypePath);
        const useRmiGauge = options.crwScope === 'rmi' && !usePacific;
        const useNativeCurrent = !!options.crwNativeAlert && outlookTypePath === 'CRW_BAA_Current_Rounded' && !usePacific;
        config.crwPacific = usePacific;
        config.view = useRmiGauge
            ? { latitude: 9.9, longitude: 166.5, zoom: 4.1, bearing: 0, pitch: 0 }
            : (usePacific ? { latitude: 5, longitude: 205, zoom: 2.05, bearing: 0, pitch: 0 } : VIEW_STATES.SECOND);
        // Native 5 km is intentionally a current-only view. The national RMI
        // gauge and Palau archive both expose every dated map they publish.
        config.sliderVisible = !useNativeCurrent;

        if (outlookTypePath && fileName) {
            if (useNativeCurrent) {
                const scope = options.crwScope === 'rmi' ? 'rmi' : 'palau';
                const date = crwDailyDates.baa;
                config.view = scope === 'rmi'
                    ? { latitude: 9.9, longitude: 166.5, zoom: 4.1, bearing: 0, pitch: 0 }
                    : VIEW_STATES.SECOND;
                config.jsonPaths = date
                    ? [`/jsonFiles/coral-reef-watch/${scope}/native/baa/${date}.fgb`, scope === 'rmi' ? '/CMIPtas/rmiEEZ.json' : '/CMIPtas/palauEEZ.json']
                    : [`/CMIPtas/${scope === 'rmi' ? 'rmiEEZ' : 'palauEEZ'}.json`];
                config.handlerKeys = ['crw', 'eez'];
                config.currentDial = [dialKey];
            } else if (usePacific) {
                // Native Pacific exports contain MultiPolygons after the land
                // cut-out; the browser FlatGeobuf worker cannot decode that
                // geometry type. The 1° tier is ordinary polygons and remains
                // a complete basin-level product.
                const res = options.crwRes === '2deg' ? '2deg' : '1deg';
                // FGB-only on disk; getJSON auto-prefers the .fgb for a .json path.
                // Keep the Palau EEZ outline on top of the whole-Pacific tile too.
                config.jsonPaths = [`/jsonFiles/CRW-BAA-Pacific/${res}/${outlookTypePath}/${fileName}.json`, `/CMIPtas/palauEEZ.json`];
                config.handlerKeys = ['crw', 'eez'];
                config.currentDial = [dialKey];
            } else if (useRmiGauge) {
                const dates = crwRmiAlertDates[outlookTypePath] || [];
                const safeIndex = Math.max(0, Math.min(Number(dateIndex) || 0, Math.max(dates.length - 1, 0)));
                const selectedDate = dates[safeIndex];
                const suffix = options.crwAlertMode === 'modified' ? '' : '_legacy';
                // Existing dated RMI archives predate the paired *_legacy
                // files, so use their published map for timeline frames. The
                // operational publisher now writes both variants going
                // forward; latest retains the viewer-specific fallback.
                const alertFile = selectedDate ? `${selectedDate}.fgb` : `latest${suffix}.fgb`;
                config.dateData = dates;
                config.crwDateIndex = safeIndex;
                config.markerData = () => dates.map((value, index) => ({ value: index, label: value }));
                config.tooltip = ({ value }) => dates[value] || selectedDate || '';
                config.sliderVisible = dates.length > 0;
                config.jsonPaths = [`/jsonFiles/CRW-BAA-RMI/${outlookTypePath}/${alertFile}`, `/CMIPtas/rmiEEZ.json`];
                config.handlerKeys = ['crw', 'eez'];
                config.currentDial = [dialKey];
            } else {
                config.jsonPaths = [`/jsonFiles/CRW-BAA/${outlookTypePath}/${fileName}.json`, `/CMIPtas/palauEEZ.json`];
                config.handlerKeys = ['crw', 'eez'];
                config.currentDial = [dialKey];
            }
        } else {
            config.jsonPaths = [`/CMIPtas/palauEEZ.json`];
        }
    }
    // --- Biochemistry ---
    else if (variable === "8.2.1") { 
        return applyOceanColorConfig(config, 'chlorophyll', dateIndex, options, 'linear');
    }
    else if (variable === "8.2.2") { 
        return applyOceanColorConfig(config, 'chlorophyll', dateIndex, options, 'logarithmic');
    }
    else if (variable === "8.3.1") { 
        return applyOceanColorConfig(config, 'phytoplankton_size', dateIndex, options);
    }

    return config;
};
